<template>
  <q-dialog v-model="showOverlay" persistent maximized transition-show="fade" transition-hide="fade">
    <div class="cache-refresh-overlay">
      <q-card class="refresh-card">
        <q-card-section class="text-center">
          <div class="text-h5">Updating DocBits</div>
          <div v-if="fromVersion && toVersion" class="text-caption text-grey-6 q-mt-xs">
            v{{ fromVersion }} &rarr; v{{ toVersion }}
          </div>
        </q-card-section>

        <q-card-section class="progress-section">
          <div class="circular-progress-wrapper">
            <q-circular-progress
              :value="progress"
              size="180px"
              :thickness="0.15"
              :color="progressColor"
              track-color="grey-3"
              class="circular-progress"
              :animation-speed="300"
            >
              <div class="progress-inner">
                <q-icon :name="stepIcon" :size="progressComplete ? '48px' : '36px'" :color="progressColor" class="step-icon" />
                <div class="progress-percentage" :class="{ complete: progressComplete }">
                  {{ progress }}%
                </div>
              </div>
            </q-circular-progress>
          </div>
        </q-card-section>

        <q-card-section class="text-center step-section">
          <span class="step-message">{{ message }}</span>
        </q-card-section>
      </q-card>
    </div>
  </q-dialog>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useCacheRefreshProgress } from 'src/utils/cacheRefreshProgress';

const {
  isActive,
  progress,
  message,
  currentStep,
  fromVersion,
  toVersion,
} = useCacheRefreshProgress();

// Local show state to control the dialog
const showOverlay = ref(false);

// Sync with reactive state
watch(isActive, (active) => {
  showOverlay.value = active;
}, { immediate: true });

const progressComplete = computed(() => progress.value === 100);

const progressColor = computed(() => {
  if (progress.value === 100) return 'positive';
  if (progress.value >= 60) return 'primary';
  return 'primary';
});

// Compute step icon based on current step
const stepIcon = computed(() => {
  const step = currentStep.value;
  if (step === 6) return 'check_circle'; // Reload step - complete
  if (step >= 5) return 'rocket_launch'; // Prepare step
  if (step >= 4) return 'cleaning_services'; // Unregister SW
  if (step >= 3) return 'delete_sweep'; // Clear caches
  if (step >= 2) return 'storage'; // Clear localStorage
  return 'update'; // Start
});
</script>

<style scoped lang="scss">
.cache-refresh-overlay {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.75);
  backdrop-filter: blur(6px);
}

.refresh-card {
  min-width: 300px;
  max-width: 360px;
  border-radius: 20px;
  padding: 24px 16px;
  background: white;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.35);
}

.progress-section {
  padding: 16px 0;
}

.circular-progress-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
}

.circular-progress {
  transition: all 0.3s ease;
}

.progress-inner {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.step-icon {
  animation: pulse 1.5s ease-in-out infinite;
  transition: all 0.3s ease;
}

.progress-percentage {
  font-size: 2rem;
  font-weight: 700;
  color: var(--q-primary);
  transition: all 0.3s ease;

  &.complete {
    color: var(--q-positive);
  }
}

.step-section {
  padding-top: 8px;
  padding-bottom: 0;
}

.step-message {
  font-size: 1rem;
  font-weight: 500;
  color: #666;
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.7;
    transform: scale(0.95);
  }
}

// Dark mode support
body.body--dark {
  .refresh-card {
    background: var(--card);
  }

  .step-message {
    color: var(--muted-foreground);
  }

  .progress-percentage {
    color: var(--q-primary);

    &.complete {
      color: var(--q-positive);
    }
  }

  :deep(.q-circular-progress__track) {
    color: rgba(255, 255, 255, 0.1);
  }
}
</style>
